import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent } from '../components/ui/dialog';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { blogPosts, blogCategories, getFeaturedPosts } from '../data/blogPosts';
import { crmAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { 
  Search, 
  Clock, 
  Calendar, 
  ArrowRight, 
  ChevronRight,
  Bookmark,
  TrendingUp,
  Sparkles,
  Play,
  Phone,
  Mail,
  MessageSquare,
  User,
  Send,
  Check,
  X
} from 'lucide-react';

const Blog = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Demo booking modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    message: ''
  });
  const [isSubmittingDemo, setIsSubmittingDemo] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name.trim()) {
      toast({ title: "Name required", description: "Please enter your name", variant: "destructive" });
      return;
    }
    if (!demoForm.email.trim() && !demoForm.phone.trim()) {
      toast({ title: "Contact required", description: "Please enter your email or phone number", variant: "destructive" });
      return;
    }
    setIsSubmittingDemo(true);
    try {
      await crmAPI.submitDemoRequest('mraja', {
        name: demoForm.name.trim(),
        email: demoForm.email.trim() || null,
        phone: demoForm.phone.trim() ? `${demoForm.countryCode}${demoForm.phone.trim()}` : null,
        message: demoForm.message.trim() || null,
        source: "Blog Page - Demo CTA"
      });
      setDemoSuccess(true);
      toast({ title: "Demo Request Sent!", description: "We'll contact you soon." });
      setTimeout(() => {
        setDemoForm({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
        setDemoSuccess(false);
        setShowDemoModal(false);
      }, 2000);
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmittingDemo(false);
    }
  };

  const featuredPosts = getFeaturedPosts();

  const filteredPosts = useMemo(() => {
    let posts = [...blogPosts];
    
    if (selectedCategory !== 'all') {
      posts = posts.filter(post => 
        post.category.toLowerCase().replace(/&/g, '').replace(/ +/g, '-').replace(/--/g, '-') === selectedCategory
      );
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      posts = posts.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.content?.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query)) ||
        post.category.toLowerCase().includes(query)
      );
    }
    
    return posts;
  }, [selectedCategory, searchQuery]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <SEO 
        title="Blog | Piper Card - Digital Business Card Insights"
        description="Expert guides on NFC business cards, QR codes, digital networking, and professional branding. Learn how to modernize your networking strategy."
        keywords={['NFC business card blog', 'digital business card guide', 'QR code tips', 'networking advice', 'professional branding']}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 pt-12 pb-8 lg:pt-20 lg:pb-12">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Insights & Guides
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">
                Piper Card <span className="text-amber-400">Blog</span>
              </h1>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Master digital networking with expert guides on NFC cards, QR codes, and professional branding
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 rounded-2xl text-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Featured Articles */}
            {featuredPosts.length > 0 && !searchQuery && selectedCategory === 'all' && (
              <div className="grid lg:grid-cols-12 gap-6 mb-8">
                {/* Main Featured */}
                <Link 
                  to={`/blog/${featuredPosts[0].slug}`}
                  className="lg:col-span-7 group relative rounded-3xl overflow-hidden aspect-[16/9] lg:aspect-[16/10]"
                >
                  <img 
                    src={featuredPosts[0].image} 
                    alt={featuredPosts[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/10 transition-colors duration-300" />
                  
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-amber-500 text-white border-0 px-3 py-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                    <Badge className="bg-white/20 text-white border-0 mb-3 backdrop-blur-sm">
                      {featuredPosts[0].category}
                    </Badge>
                    <h2 className="text-2xl lg:text-4xl font-bold text-white mb-3 group-hover:text-amber-300 transition-colors leading-tight">
                      {featuredPosts[0].title}
                    </h2>
                    <p className="text-gray-300 mb-4 line-clamp-2 text-lg hidden sm:block">
                      {featuredPosts[0].excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(featuredPosts[0].date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {featuredPosts[0].readTime}
                      </span>
                    </div>
                  </div>
                </Link>

                {/* Secondary Featured */}
                <div className="lg:col-span-5 flex flex-col gap-4">
                  {featuredPosts.slice(1, 4).map((post, index) => (
                    <Link 
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="group flex gap-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 hover:bg-gray-800 transition-all duration-300 border border-gray-700/50 hover:border-amber-500/50"
                    >
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-24 h-24 lg:w-28 lg:h-28 object-cover rounded-xl flex-shrink-0 group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="flex flex-col justify-center min-w-0">
                        <Badge className="bg-gray-700/50 text-gray-300 border-0 w-fit mb-2 text-xs">
                          {post.category}
                        </Badge>
                        <h3 className="font-semibold text-white group-hover:text-amber-400 transition-colors line-clamp-2 text-sm lg:text-base">
                          {post.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
                          <span>{formatDate(post.date)}</span>
                          <span>•</span>
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-gray-50 rounded-t-[3rem] -mt-8 relative z-10">
          <div className="max-w-7xl mx-auto px-4 py-12 lg:py-16">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-10 justify-center">
              {blogCategories.slice(0, 10).map((category) => (
                <Button
                  key={category.slug}
                  variant={selectedCategory === category.slug ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category.slug)}
                  className={`rounded-full px-5 transition-all duration-300 ${
                    selectedCategory === category.slug 
                      ? 'bg-gray-900 hover:bg-gray-800 text-white shadow-lg' 
                      : 'bg-white hover:bg-gray-100 border-gray-200 text-gray-700'
                  }`}
                >
                  {category.name}
                </Button>
              ))}
            </div>

            {/* Section Title */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedCategory === 'all' ? 'All Articles' : blogCategories.find(c => c.slug === selectedCategory)?.name}
              </h2>
              <span className="text-gray-500 text-sm">{filteredPosts.length} articles</span>
            </div>

            {/* Articles Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <Card 
                  key={post.id} 
                  className={`overflow-hidden hover:shadow-2xl transition-all duration-500 group cursor-pointer border-0 bg-white rounded-2xl ${
                    index === 0 && selectedCategory === 'all' && !searchQuery ? 'md:col-span-2 lg:col-span-1' : ''
                  }`}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <div className="aspect-[16/10] relative overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <Badge className="absolute top-4 left-4 bg-white/95 text-gray-900 border-0 shadow-lg">
                      {post.category}
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl text-gray-900 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2 leading-tight">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{formatDate(post.date)}</span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span>{post.readTime}</span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Bookmark className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
                <Button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Upgrade Your Networking?
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Join thousands of professionals using Piper Card for smarter networking
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-amber-600 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
                onClick={() => navigate('/shop')}
              >
                Get Your NFC Card
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl"
                onClick={() => setShowDemoModal(true)}
                data-testid="blog-list-demo-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Book Live Demo
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Booking Modal */}
      <Dialog open={showDemoModal} onOpenChange={(open) => {
        if (!isSubmittingDemo) {
          setShowDemoModal(open);
          if (!open) {
            setDemoForm({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
            setDemoSuccess(false);
          }
        }
      }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-5 text-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Book Your Live Demo</h2>
                  <p className="text-sm text-white/80">Get a personalized walkthrough</p>
                </div>
              </div>
              <button 
                onClick={() => !isSubmittingDemo && setShowDemoModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {demoSuccess ? (
            <div className="px-5 py-10 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Request Sent!</h3>
              <p className="text-gray-600">We'll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleDemoSubmit} className="px-5 py-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-violet-500" />
                  Your Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  placeholder="Enter your full name"
                  value={demoForm.name}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-12 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail className="w-4 h-4 text-violet-500" />
                  Email
                </Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={demoForm.email}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full h-12 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-violet-500" />
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <CountryCodeSelect
                    value={demoForm.countryCode}
                    onChange={(e) => setDemoForm(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="w-24 h-12 rounded-xl"
                  />
                  <Input
                    type="tel"
                    inputMode="numeric"
                    placeholder="9876543210"
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm(prev => ({ ...prev, phone: e.target.value.replace(/[^\d]/g, '') }))}
                    className="flex-1 h-12 rounded-xl"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-violet-500" />
                  Message <span className="text-gray-400 font-normal">(Optional)</span>
                </Label>
                <textarea
                  placeholder="Tell us about your requirements..."
                  value={demoForm.message}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  rows={3}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmittingDemo}
                className="w-full h-12 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl"
              >
                {isSubmittingDemo ? (
                  <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Sending...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" />Book Live Demo</>
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Blog;
